// WARNING: There is no validation of native Button props that <ButtonProg> forwards to <Button>
// So a simple mistake such as:
//   onClick={setConfirmDialogIsOpen(true)}
// instead of:
//   onClick={() => setConfirmDialogIsOpen(true)}
// won't be caught by the compile or build steps, and can result in hard-to-debug errors.
// (In the above: an error about infinite rendering being disabled by React) Be careful!
import { css } from '@emotion/css';
import { useEffect, useState } from 'react';
import { Button, ButtonProps } from 'antd';
import classNames from 'classnames';
import { green, red } from '@ant-design/colors';

const successBtnCls = css`
  &.ant-btn,
  &.ant-btn[disabled] {
    border-color: ${green[2]};
    &:hover {
      border-color: ${green[1]};
    }
  }
  &.ant-btn[disabled]:hover {
    border-color: ${green[2]};
  }
`;
const errorBtnCls = css`
  &.ant-btn,
  &.ant-btn[disabled] {
    border-color: ${red[2]};
    &:hover {
      border-color: ${red[1]};
    }
  }
  &.ant-btn[disabled]:hover {
    border-color: ${red[2]};
  }
`;

export const ButtonProg = function ({
  children,
  className,
  icon,
  loading,
  error,
  disabled,
  preventFade = false,
  ...buttonProps
}: ButtonProps & { error: boolean; preventFade?: boolean }): JSX.Element {
  const [previouslyLoading, setPrevLoading] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (previouslyLoading && !loading && !error) {
      setShowSuccess(true);
    } else if (previouslyLoading && !loading) {
      setShowError(true);
    }
    setPrevLoading(!!loading);
  }, [previouslyLoading, loading, error]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!preventFade && showSuccess) {
      timeout = setTimeout(() => setShowSuccess(false), 1000);
    }
    return (): void => {
      clearTimeout(timeout);
    };
  }, [preventFade, showSuccess]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!preventFade && showError) {
      timeout = setTimeout(() => setShowError(false), 1000);
    }
    return (): void => {
      clearTimeout(timeout);
    };
  }, [preventFade, showError]);

  return (
    <Button
      className={classNames(
        { [successBtnCls]: showSuccess, [errorBtnCls]: showError },
        className
      )}
      icon={icon}
      disabled={!!loading || disabled}
      loading={loading}
      {...buttonProps}
    >
      {children}
    </Button>
  );
};

export default ButtonProg;
